import * as React from 'react';
import { useFormContext, type FieldPath, type FieldValues } from 'react-hook-form';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  RequiredMark,
} from './Form';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';

interface FormInputProps<TValues extends FieldValues>
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'name' | 'form'> {
  name: FieldPath<TValues>;
  label?: React.ReactNode;
  description?: React.ReactNode;
  /** A password field with a show/hide toggle; `type` is ignored. */
  revealable?: boolean;
}

export function FormInput<TValues extends FieldValues>({
  name,
  label,
  description,
  required,
  revealable,
  ...inputProps
}: FormInputProps<TValues>) {
  const { control } = useFormContext<TValues>();
  const Control = revealable ? PasswordInput : Input;
  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem>
          {label !== undefined && (
            <FormLabel>
              {label}
              {required && <RequiredMark />}
            </FormLabel>
          )}
          <FormControl>
            <Control
              {...inputProps}
              required={required}
              {...field}
              value={(field.value as string | number | undefined) ?? ''}
            />
          </FormControl>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
